// The four states an Olo webhook connection can be in, mirroring exactly what
// deriveOloWebhookConnectionState + isOloWebhookStale (oloWebhookStatus.ts)
// derive from a credential's `configured` flag and a receipt's
// `last_received_at`: not yet configured, configured but nothing has arrived
// yet, configured and stale (>= STALE_AFTER_MS since the last event), and
// configured and healthy. Timestamps are computed relative to `now` at call
// time (not baked in as static ISO strings) so "received recently"/"a while
// ago" stay accurate no matter how long a dev session stays open.
import { STALE_AFTER_MS } from 'utilities/oloWebhookStatus'

export type OloWebhookPresetKey =
  | 'not_configured'
  | 'waiting_for_first'
  | 'received_a_while_ago'
  | 'received_recently'

export type OloWebhookPreset = {
  key: OloWebhookPresetKey
  label: string
  configured: boolean
  lastReceivedAt: string | null
}

// Comfortably inside the 24h staleness window.
const RECEIVED_RECENTLY_MS = 5 * 60 * 1000
// Just past the staleness threshold, so this preset reliably reads as stale
// regardless of clock skew between building the preset and the UI reading it.
const RECEIVED_A_WHILE_AGO_MS = STALE_AFTER_MS + 60 * 60 * 1000

export const DEFAULT_OLO_WEBHOOK_PRESET_KEY: OloWebhookPresetKey =
  'received_recently'

export function buildOloWebhookPresets(
  now: number = Date.now()
): OloWebhookPreset[] {
  return [
    {
      key: 'not_configured',
      label: 'Not configured',
      configured: false,
      lastReceivedAt: null,
    },
    {
      key: 'waiting_for_first',
      label: 'Configured — waiting for first webhook',
      configured: true,
      lastReceivedAt: null,
    },
    {
      key: 'received_a_while_ago',
      label: 'Configured — received a while ago (stale)',
      configured: true,
      lastReceivedAt: new Date(now - RECEIVED_A_WHILE_AGO_MS).toISOString(),
    },
    {
      key: 'received_recently',
      label: 'Configured — received recently',
      configured: true,
      lastReceivedAt: new Date(now - RECEIVED_RECENTLY_MS).toISOString(),
    },
  ]
}

export function findOloWebhookPreset(
  key: OloWebhookPresetKey,
  now: number = Date.now()
): OloWebhookPreset {
  const preset = buildOloWebhookPresets(now).find(
    candidate => candidate.key === key
  )
  // Unreachable with a valid OloWebhookPresetKey — the fallback just keeps
  // this total instead of possibly-undefined for callers.
  return preset ?? buildOloWebhookPresets(now)[0]
}
